import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Stack,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import api from "../api/axios";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
);

export default function CumulativeMeterageChart() {
  const [loading, setLoading] = useState(true);
  const [navigation, setNavigation] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [summary, setSummary] = useState({
    mtd_day: 0,
    current_mtd: 0,
    prev_mtd: 0,
    growth_percentage: 0,
  });

  const fetchData = async (month = "") => {
    try {
      setLoading(true);
      const { data } = await api.get("/dashboard/cumulative-meterage-chart", {
        params: month ? { month } : {},
      });
      setNavigation(data.navigation);
      setChartData(data.chart_data || []);

      setSummary(
        data.summary || {
          mtd_day: 0,
          current_mtd: 0,
          prev_mtd: 0,
          growth_percentage: 0,
        },
      );
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const meterageDifference =
    Number(summary.current_mtd || 0) - Number(summary.prev_mtd || 0);

  const growthPercentage = Number(summary.growth_percentage || 0);

  const isGrowth = meterageDifference >= 0;

  const formatMeterage = (value) =>
    Math.abs(value).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const data = useMemo(
    () => ({
      labels: chartData.map((r) => r.day),
      datasets: [
        {
          label: "Borehole Closures",
          data: chartData.map((r) => r.cumulative_meterage),
          fill: true,
          borderColor: "#22c55e",
          backgroundColor: "rgba(34,197,94,0.18)",
          borderWidth: 3,
          tension: 0.35,
          pointRadius: chartData.map((r) => (r.markers?.length ? 7 : 0)),
          pointHoverRadius: chartData.map((r) => (r.markers?.length ? 9 : 5)),
          pointBackgroundColor: chartData.map((r) =>
            r.markers?.length ? r.markers[0].color : "transparent",
          ),
          pointBorderColor: "#ffffff",
          pointBorderWidth: chartData.map((r) => (r.markers?.length ? 2 : 0)),
        },
      ],
    }),
    [chartData],
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: {
          labels: { color: "#fff", usePointStyle: true },
        },
        tooltip: {
          backgroundColor: "#222",
          titleColor: "#fff",
          bodyColor: "#fff",
          padding: 10,

          callbacks: {
            title: (tooltipItems) => {
              const index = tooltipItems[0]?.dataIndex;
              const dayData = chartData[index];

              if (!dayData) return "";

              if (dayData.markers?.length > 0) {
                return `Borehole Closure · ${dayData.day} ${
                  navigation?.display_name || ""
                }`;
              }

              return `${dayData.day} ${navigation?.display_name || ""}`;
            },

            label: (context) => {
              const index = context.dataIndex;
              const dayData = chartData[index];

              if (!dayData?.markers?.length) {
                return `Cumulative Meterage: ${Number(
                  dayData?.cumulative_meterage || 0,
                ).toFixed(2)} m`;
              }

              return "";
            },

            afterBody: (tooltipItems) => {
              const index = tooltipItems[0]?.dataIndex;
              const dayData = chartData[index];

              if (!dayData?.markers?.length) {
                return [];
              }

              const lines = [];

              dayData.markers.forEach((marker, markerIndex) => {
                if (markerIndex > 0) {
                  lines.push("");
                }

                lines.push(
                  `${marker.bh_code || `BH-${marker.bh_id}`} · ${
                    marker.project_name
                  }`,
                );

                lines.push(
                  `Total Depth: ${Number(marker.total_meterage || 0).toFixed(
                    2,
                  )} m`,
                );

                lines.push(
                  `Rig: ${marker.rig_name || "-"} in ${
                    marker.days_spent ?? "-"
                  } days`,
                );
              });

              return lines;
            },
          },
        },
      },
      scales: {
        x: {
          ticks: { color: "#ddd" },
          grid: { color: "rgba(255,255,255,0.06)" },
        },
        y: {
          beginAtZero: true,
          ticks: { color: "#ddd" },
          title: {
            display: true,
            text: "Running Meterage (m)",
            color: "#ddd",
          },
          grid: { color: "rgba(255,255,255,0.08)" },
        },
      },
    }),
    [chartData],
  );

  return (
    <Box
      sx={{
        mt: 3,
        p: 3,
        borderRadius: 3,
        backdropFilter: "blur(12px)",
        background: "rgba(255,255,255,0.05)",
        border: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <IconButton
          disabled={!navigation?.prev_month}
          onClick={() => fetchData(navigation.prev_month)}
        >
          <ArrowBackIcon />
        </IconButton>

        <Box sx={{ textAlign: "center", minWidth: 0 }}>
          <Typography
            variant="h6"
            fontWeight={600}
            sx={{
              fontSize: {
                xs: "0.9rem",
                sm: "1.1rem",
              },
            }}
          >
            Monthly Cumulative Drilling Meterage
          </Typography>

          <Typography
            component="div"
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1 }}
          >
            {navigation?.display_name}
          </Typography>

          {/* MTD COMPARISON */}
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            justifyContent="center"
            sx={{
              color: isGrowth ? "#4CAF50" : "#F44336",
              flexWrap: "wrap",
            }}
          >
            {isGrowth ? (
              <TrendingUpIcon sx={{ fontSize: 20 }} />
            ) : (
              <TrendingDownIcon sx={{ fontSize: 20 }} />
            )}

            <Typography
              component="span"
              sx={{
                fontSize: "0.85rem",
                fontWeight: 700,
              }}
            >
              {formatMeterage(meterageDifference)} m
            </Typography>

            <Typography
              component="span"
              sx={{
                fontSize: "0.8rem",
                fontWeight: 600,
              }}
            >
              ({Math.abs(growthPercentage).toFixed(2)}%)
            </Typography>
          </Stack>

          <Typography
            component="div"
            sx={{
              fontSize: "0.65rem",
              color: "text.secondary",
              mt: 0.3,
            }}
          >
            Month-to-Date: Current Month {formatMeterage(summary.current_mtd)} m
            vs Previous Month: {formatMeterage(summary.prev_mtd)} m
          </Typography>
        </Box>

        <IconButton
          disabled={!navigation?.next_month}
          onClick={() => fetchData(navigation.next_month)}
        >
          <ArrowForwardIcon />
        </IconButton>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ height: 420 }}>
          <Line data={data} options={options} />
        </Box>
      )}
    </Box>
  );
}
